import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Sparkles, FileText, MessageSquare, Briefcase, Target, Route, Award, CheckCircle, TrendingUp, Crown } from "lucide-react";

const ICONS = { sparkles: Sparkles, "file-text": FileText, "message-square": MessageSquare, briefcase: Briefcase, target: Target, trophy: Trophy, award: Award, route: Route, "check-circle": CheckCircle, "trending-up": TrendingUp, crown: Crown };

export default function Achievements() {
  const [progress, setProgress] = useState(null);

  useEffect(() => { api.get("/progress").then(r => setProgress(r.data)); }, []);
  if (!progress) return <div className="p-10">Loading...</div>;
  const pct = progress.progress_pct || 0;
  const earned = progress.all_badges.filter(b => b.earned).length;
  const total = progress.all_badges.length;

  return (
    <div className="p-10 max-w-5xl" data-testid="achievements-page">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 08</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">Achievements</h1>
      </div>

      {/* Level card */}
      <div className="card-flat p-8 mb-8 grid md:grid-cols-[auto_1fr_auto] gap-8 items-center" data-testid="level-card">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 100 100" className="w-32 h-32">
            <circle cx="50" cy="50" r="45" stroke="#2a2a2a" strokeWidth="6" fill="none"/>
            <circle cx="50" cy="50" r="45" stroke="#0F52BA" strokeWidth="6" fill="none" strokeDasharray={`${(pct/100)*283} 283`} strokeLinecap="round" transform="rotate(-90 50 50)"/>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Level</div>
            <div className="font-display font-black text-4xl">{progress.level}</div>
          </div>
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Career OS Level</div>
          <div className="font-display font-bold text-3xl tracking-tight mb-3">{progress.xp.toLocaleString()} XP</div>
          <div className="h-2 bg-secondary mb-2"><div className="h-full bg-primary" style={{ width: `${pct}%` }}/></div>
          <div className="text-xs font-mono text-muted-foreground">{progress.xp_for_next_level - progress.xp} XP to Level {progress.level + 1}</div>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Badges</div>
          <div className="font-display font-black text-4xl">{earned}<span className="text-lg text-muted-foreground font-mono">/{total}</span></div>
        </div>
      </div>

      {/* Badges grid */}
      <div className="font-display font-bold text-2xl tracking-tight mb-4">Badges</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="badges-grid">
        {progress.all_badges.map(b => {
          const Icon = ICONS[b.icon] || Sparkles;
          return (
            <div key={b.id} className={`card-flat p-5 text-center transition-opacity ${b.earned ? "" : "opacity-30"}`} data-testid={`badge-${b.id}${b.earned?"-earned":""}`}>
              <div className={`w-12 h-12 mx-auto mb-3 flex items-center justify-center ${b.earned ? "bg-primary" : "bg-accent border border-border"}`}>
                <Icon size={20} className="text-foreground"/>
              </div>
              <div className="font-display font-bold text-sm mb-1">{b.name}</div>
              <div className="text-[10px] text-muted-foreground leading-snug">{b.description}</div>
              {b.earned && <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-emerald-400">Earned</div>}
            </div>
          );
        })}
      </div>

      <div className="mt-8 card-flat p-5 text-sm text-muted-foreground">
        <div className="font-display font-bold text-foreground mb-1">Earn XP by:</div>
        <div className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono uppercase tracking-widest">
          <div>Uploading resume · <span className="text-emerald-400">+75</span></div>
          <div>Completing profile · <span className="text-emerald-400">+100</span></div>
          <div>Sending advisor chat · <span className="text-emerald-400">+5</span></div>
          <div>Adding a job application · <span className="text-emerald-400">+25</span></div>
          <div>Reaching interview stage · <span className="text-emerald-400">+40</span></div>
          <div>Landing an offer · <span className="text-emerald-400">+200</span></div>
          <div>Adding certification · <span className="text-emerald-400">+30</span></div>
          <div>Running ATS score · <span className="text-emerald-400">+20</span></div>
        </div>
      </div>
    </div>
  );
}
